import { SplitedRequests } from './filterAndSearchUtils';
import { AskRequest } from '@hone-automation/common/lib/AskRequests';

/**
 * Selects an element from either the "me" or "team" array based on updated date
 * @param {Object} data - Object containing "me" and "team" arrays
 * @param {string} sortOrder - Either "newest" or "oldest"
 * @returns {Object|null} - The selected element or null if both arrays are empty
 */
function selectElement(data: SplitedRequests, sortOrder = 'newest') {
  // Check if data and necessary properties exist
  if (!data || (!data.me && !data.team)) {
    return null;
  }

  // Determine which array to use (me if it has elements, otherwise team)
  const sourceArray =
    Array.isArray(data.me) && data.me.length > 0
      ? data.me
      : Array.isArray(data.team) && data.team.length > 0
        ? data.team
        : null;

  // Return null if both arrays are empty
  if (!sourceArray || sourceArray.length === 0) {
    return null;
  }

  // Sort the array by createdAt timestamp
  const sortedArray = [...sourceArray].sort((a, b) => {
    const dateA = new Date(a.updatedAt).valueOf();
    const dateB = new Date(b.updatedAt).valueOf();

    // For "newest", we want descending order (most recent first)
    // For "oldest", we want ascending order (oldest first)
    return sortOrder === 'newest'
      ? dateB - dateA // Newest first
      : dateA - dateB; // Oldest first
  });

  // Return the first element of the sorted array
  return sortedArray[0];
}

/**
 * Selects an element from an array of AskRequest based on priority and updated date
 * @param {AskRequest[]} data - Array of AskRequest objects
 * @param {string} sortOrder - Either "newest" or "oldest"
 * @returns {AskRequest|null} - The selected element or null if the array is empty
 */
export function selectAskRequestElement(data: AskRequest[], sortOrder = 'newest') {
  // Check if data exists and is not empty
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  // First, check if there are any blockers
  // @ts-ignore
  const blockers = data.filter(request => request.priority === 'Blocker');

  // If we have blockers, prioritize them
  if (blockers.length > 0) {
    // Sort blockers by updatedAt timestamp
    const sortedBlockers = [...blockers].sort((a, b) => {
      const dateA = new Date(a.updatedAt).valueOf();
      const dateB = new Date(b.updatedAt).valueOf();

      return sortOrder === 'newest'
        ? dateB - dateA // Newest first
        : dateA - dateB; // Oldest first
    });

    // Return the first blocker
    return sortedBlockers[0];
  }

  // If no blockers, fallback to the regular sorting
  const sortedArray = [...data].sort((a, b) => {
    const dateA = new Date(a.updatedAt).valueOf();
    const dateB = new Date(b.updatedAt).valueOf();

    // For "newest", we want descending order (most recent first)
    // For "oldest", we want ascending order (oldest first)
    return sortOrder === 'newest'
      ? dateB - dateA // Newest first
      : dateA - dateB; // Oldest first
  });

  // Return the first element of the sorted array
  return sortedArray[0];
}

export default selectElement;
